import React from 'react';
import * as Yup from 'yup';
import { memoize } from 'lodash';
import { useFormik } from 'formik';

import Show from 'components/Show';
import Heading from 'components/common/Heading';
import { Error, Field, Label, Select } from 'components/common/Input/Input';

import { Storage } from 'services/storage';
import { UserService } from 'services/UserService';
import { IntlMessage } from 'util/IntlMessages';
import { Notify } from 'util/notify';
import { isBoolean, isEmpty } from 'util/utils';
import { useIsMobile } from 'util/hooks';

const stkNotiDtOptions = [
  { label: 'Daily', value: '1' },
  { label: 'Weekly', value: '2' },
  { label: 'Monthly', value: '3' },
];

const stkNotiOptions = [
  { label: 'New Stock', value: '1' },
  { label: 'New & Revised Stock', value: '2' },
  { label: 'Full Stock', value: '3' },
];

const defaultValues = {
  stkNotiDt: undefined,
  stkNoti: undefined,
  isStkNoti: false,
};

const validationSchema = Yup.object().shape({
  stkNotiDt: Yup.string()
    .oneOf(
      stkNotiOptions.map(({ value }) => value),
      'Please, select a valid option',
    )
    .required('Please, select an option')
    .nullable(),
  stkNoti: Yup.string()
    .oneOf(
      stkNotiDtOptions.map(({ value }) => value),
      'Please, select a valid option',
    )
    .required('Please, select an option')
    .nullable(),
  isStkNoti: Yup.boolean().nullable(),
});

function StockNotification() {
  const [isMobile] = useIsMobile();
  const [user, setUser] = React.useState(Storage.get('user') ?? {});

  const initialValues = React.useMemo(() => {
    const values = {
      stkNotiDt: user?.stkNotiDt ?? defaultValues?.stkNotiDt,
      stkNoti: user?.stkNoti?.[0] ?? defaultValues?.stkNoti,
      isStkNoti: user?.isStkNoti ?? defaultValues?.isStkNoti,
    };

    return {
      stkNotiDt: !isEmpty(values?.stkNotiDt) ? String(values?.stkNotiDt) : undefined,
      stkNoti: !isEmpty(values?.stkNoti) ? String(values?.stkNoti) : undefined,
      isStkNoti: isBoolean(values?.isStkNoti) ? Boolean(values?.isStkNoti) : false,
    };
  }, [user?.isStkNoti, user?.stkNoti, user?.stkNotiDt]);

  const onSubmit = React.useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(true);

      values = {
        ...values,
        stkNotiDt: !isEmpty(values?.stkNotiDt) ? Number(values?.stkNotiDt) : undefined,
        stkNoti: !isEmpty(values?.stkNoti) ? [Number(values?.stkNoti)] : undefined,
        isStkNoti: isBoolean(values?.isStkNoti) ? Boolean(values?.isStkNoti) : false,
      };

      const [err, res] = await UserService.updateUserProfile({ id: user?.id, ...values });

      if (!err && res?.code === 'OK') {
        const newUser = { ...user, ...res?.data?.[0], ...values };
        Storage.set('user', newUser);
        Notify.success({ message: 'Settings updated' });
      }

      setSubmitting(false);
    },
    [user],
  );

  const onReset = React.useCallback(() => {
    // setTimeout(() => {}, 0);
  }, []);

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnBlur: true,
    onSubmit,
    onReset,
  });
  const { setValues, setFieldTouched, handleChange, handleBlur, handleSubmit, handleReset, resetForm } = formik;
  const { values, touched, errors, isSubmitting } = formik;

  const setFieldValue = React.useMemo(() => {
    return memoize(
      (field) => (value) => {
        setValues((_values) => ({ ..._values, [field]: value }), false);
        setTimeout(() => setFieldTouched(field, true, true), 0);
      },
      (...args) => JSON.stringify(args),
    );
  }, [setFieldTouched, setValues]);

  const formActionsDisabled = isSubmitting; // || !dirty;

  React.useEffect(() => {
    return Storage.listen('user', (data) => {
      setUser(data?.user);
      resetForm();
    });
  }, [resetForm]);

  return (
    <React.Fragment>
      <div className="profileHead">
        <Show when={isMobile}>
          <Heading className="popupInnerTitle">
            <IntlMessage id="app.StockNotification" />
          </Heading>
        </Show>
      </div>
      <div className="profileDetailSpace">
        <Show when={isSubmitting}>
          <div className="loading-indicator"></div>
        </Show>
        <form id="StockNotificationForm" onSubmit={handleSubmit} onReset={handleReset}>
          <IntlMessage id="app.sendMeMailAt">
            {(placeholder) => {
              return (
                <Field hasErrors={touched?.stkNotiDt && !isEmpty(errors?.stkNotiDt)}>
                  <Label htmlFor="stkNoti" required>
                    {placeholder}
                  </Label>
                  <Select
                    name="stkNoti"
                    value={values?.stkNotiDt}
                    placeholder="Select Interval"
                    showAction={['focus', 'click']}
                    dropdownClassName="selectDopdown"
                    onChange={setFieldValue('stkNotiDt')}
                  >
                    {stkNotiDtOptions.map(({ value, label }, index) => (
                      <Select.Option key={`${index}-${value}`} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Error>{touched?.stkNotiDt && errors?.stkNotiDt}</Error>
                </Field>
              );
            }}
          </IntlMessage>
          <IntlMessage id="app.sendMeStock">
            {(placeholder) => {
              return (
                <Field hasErrors={touched?.stkNoti && !isEmpty(errors?.stkNoti)}>
                  <Label htmlFor="stkNoti" required>
                    {placeholder}
                  </Label>
                  <Select
                    name="stkNoti"
                    value={values?.stkNoti}
                    placeholder="Select Stock Type"
                    showAction={['focus', 'click']}
                    dropdownClassName="selectDopdown"
                    onChange={setFieldValue('stkNoti')}
                  >
                    {stkNotiOptions.map(({ value, label }, index) => (
                      <Select.Option key={`${index}-${value}`} value={value}>
                        {label}
                      </Select.Option>
                    ))}
                  </Select>
                  <Error>{touched?.stkNoti && errors?.stkNoti}</Error>
                </Field>
              );
            }}
          </IntlMessage>
          <IntlMessage id="app.doYouWantToGetMail">
            {(placeholder) => {
              return (
                <Field hasErrors={touched?.isStkNoti && !isEmpty(errors?.isStkNoti)}>
                  <Label>
                    <input
                      name="isStkNoti"
                      type="checkbox"
                      checked={values?.isStkNoti}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    &nbsp;
                    {placeholder}
                  </Label>
                  <br />
                  <Error>{touched?.isStkNoti && errors?.isStkNoti}</Error>
                </Field>
              );
            }}
          </IntlMessage>
          <div className="botoomStickyBar diamondListingAction justify-content-center">
            <button
              className="bottomStickyButton"
              form="StockNotificationForm"
              type="submit"
              value="submit"
              disabled={formActionsDisabled}
            >
              <IntlMessage id="app.Save" />
            </button>
            <button
              className="bottomStickyButton"
              form="StockNotificationForm"
              type="reset"
              value="reset"
              // disabled={formActionsDisabled}
            >
              <IntlMessage id="app.Reset" />
            </button>
          </div>
        </form>
      </div>
    </React.Fragment>
  );
}

export default React.memo(StockNotification);
